import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { EmptyState } from "@/components/shared/empty-state";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { ROUTES } from "@/app/routes";
import { toastMutationError } from "@/lib/utils/formatting";
import {
  getGetApiAgentsAgentIdSkillsQueryKey,
  getGetApiSkillsQueryKey,
  useGetApiAgentsAgentIdSkills,
  useGetApiSkills,
  usePutApiAgentsAgentIdSkills,
} from "@/lib/api/generated/skills/skills";
import type { TenantSkillDto } from "@/lib/api/generated/control-api.schemas";
import { AgentTabSurface } from "./agent-tab-surface";

export function AgentSkillsTab() {
  const { agentId } = useParams<{ agentId: string }>();
  const queryClient = useQueryClient();
  const { data: skills = [], isLoading: isLoadingSkills } = useGetApiSkills();
  const { data: grants = [], isLoading: isLoadingGrants } = useGetApiAgentsAgentIdSkills(agentId!, {
    query: { enabled: Boolean(agentId) },
  });
  const saveGrants = usePutApiAgentsAgentIdSkills();
  const grantedIds = useMemo(
    () => new Set(grants.filter((grant) => grant.enabled).map((grant) => grant.tenantSkillId)),
    [grants],
  );
  const [draftIds, setDraftIds] = useState<Set<string> | null>(null);
  const [detailsSkillId, setDetailsSkillId] = useState<string | null>(null);
  const selectedIds = draftIds ?? grantedIds;
  const sortedSkills = useMemo(
    () => [...skills].toSorted((left, right) => left.skillKey.localeCompare(right.skillKey)),
    [skills],
  );
  const detailsSkill = useMemo(
    () => sortedSkills.find((skill) => skill.tenantSkillId === detailsSkillId) ?? null,
    [detailsSkillId, sortedSkills],
  );

  if (isLoadingSkills || isLoadingGrants) {
    return <CardSkeleton />;
  }

  if (!agentId) {
    return null;
  }

  if (sortedSkills.length === 0) {
    return <EmptyState title="No skills" description="Create tenant skills before granting them." />;
  }

  const toggle = (skillId: string) => {
    const next = new Set(selectedIds);
    if (next.has(skillId)) {
      next.delete(skillId);
    } else {
      next.add(skillId);
    }
    setDraftIds(next);
  };

  const save = () => {
    saveGrants.mutate(
      {
        agentId,
        data: {
          tenantSkillIds: [...selectedIds],
        },
      },
      {
        onSuccess: () => {
          setDraftIds(null);
          void queryClient.invalidateQueries({ queryKey: getGetApiAgentsAgentIdSkillsQueryKey(agentId) });
          void queryClient.invalidateQueries({ queryKey: getGetApiSkillsQueryKey() });
          toast.success("Agent skills saved");
        },
        onError: (error) => {
          toast.error(toastMutationError(error, "Failed to save agent skills"));
        },
      },
    );
  };

  return (
    <>
      <AgentTabSurface
        title="Skills"
        description="Choose the tenant-owned skills this agent can load during a run."
        actions={
          draftIds ? (
            <Button onClick={save} disabled={saveGrants.isPending}>
              {saveGrants.isPending ? "Saving..." : "Save"}
            </Button>
          ) : null
        }
      >
        <div className="flex flex-col overflow-hidden rounded-[var(--radius-md)] bg-[var(--bg-elevated)]">
          {sortedSkills.map((skill) => {
            const checked = selectedIds.has(skill.tenantSkillId);
            const checkboxId = `agent-skill-${skill.tenantSkillId}`;
            return (
              <div
                key={skill.tenantSkillId}
                className="flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--bg-hover)]"
              >
                <Checkbox
                  id={checkboxId}
                  checked={checked}
                  className="mt-1"
                  onCheckedChange={() => toggle(skill.tenantSkillId)}
                />
                <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium leading-5" style={{ color: "var(--text-strong)" }}>
                      {skill.displayName}
                    </span>
                    {skill.status !== "active" ? <Badge variant="secondary">{skill.status}</Badge> : null}
                    {checked ? <Badge variant="outline">Granted</Badge> : null}
                  </div>
                  {skill.description ? (
                    <p className="max-w-3xl text-sm leading-5" style={{ color: "var(--muted-foreground)" }}>
                      {skill.description}
                    </p>
                  ) : null}
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDetailsSkillId(skill.tenantSkillId)}
                >
                  <Eye className="size-4" />
                  Details
                </Button>
              </div>
            );
          })}
        </div>
      </AgentTabSurface>

      <SkillDetailsSheet
        skill={detailsSkill}
        open={Boolean(detailsSkill)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailsSkillId(null);
          }
        }}
      />
    </>
  );
}

function SkillDetailsSheet({
  skill,
  open,
  onOpenChange,
}: {
  skill: TenantSkillDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const primaryFile = skill?.files.find((file) => file.relativePath === "SKILL.md") ?? skill?.files[0];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{skill?.displayName ?? "Skill details"}</SheetTitle>
          <SheetDescription>{skill?.description || "Tenant-owned skill available to this agent."}</SheetDescription>
        </SheetHeader>

        {skill ? (
          <div className="flex flex-1 flex-col gap-5 px-4 pb-4">
            <div className="grid gap-3 rounded-md border p-3 text-sm">
              <DetailRow label="Key" value={skill.skillKey} mono />
              <DetailRow label="Status" value={skill.status} />
              <DetailRow label="Version" value={`v${skill.version}`} />
              <DetailRow label="Files" value={String(skill.files.length)} />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                Files
              </p>
              <div className="flex flex-wrap gap-2">
                {skill.files.map((file) => (
                  <Badge key={file.tenantSkillFileId} variant="outline" className="font-mono">
                    {file.relativePath}
                  </Badge>
                ))}
              </div>
            </div>

            {primaryFile ? (
              <div className="space-y-2">
                <p className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                  Preview
                </p>
                <pre className="max-h-80 overflow-auto rounded-md border bg-[var(--bg-subtle)] p-3 text-xs leading-5 whitespace-pre-wrap">
                  {primaryFile.content}
                </pre>
              </div>
            ) : null}
          </div>
        ) : null}

        {skill ? (
          <SheetFooter>
            <Button asChild variant="outline">
              <Link to={ROUTES.tenantSkill("", skill.tenantSkillId)}>Open full editor</Link>
            </Button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="grid gap-1 sm:grid-cols-[7rem_1fr]">
      <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
        {label}
      </span>
      <span className={mono ? "break-all font-mono text-xs" : "text-sm"} style={{ color: "var(--text-strong)" }}>
        {value}
      </span>
    </div>
  );
}
